// @flow

import { Fragment, type Node, useEffect } from 'react';

import { Global } from '@emotion/core';
import { isolateComponent } from '@zapier/common-utils';
import { useUniversalNavContext } from '@zapier/universal-nav';

/** @jsx isolateComponent */

type Props = {
  /**
   * Content to render within the page.
   */
  children: Node,
};

const Styles = {
  '.server-node--accommodate-universal-sidebar': {
    transition: 'all ease-in-out 0.25s',
    willChange: 'padding-left',
  },
  // This is a holdover from the old Django nodes. This node needs to
  // display like `UniversalNav` based on the DOM structure that is
  // rendered by Django.
  '#header[data-type="universal-nav"]': {
    display: 'contents',
  },
};

const WebUniversalNavPageLayout = (props: Props) => {
  const {
    isSidebarRendered,
    isSidebarControlledByTopbar,
    sidebarWidth,
  } = useUniversalNavContext();
  // Whenever the sidebar un/renders, all of these server-rendered
  // nodes need to be un/padded. Padding `body` doesn't work because
  // it can push content over that shouldn't be pushed over and render
  // a gap on the page. By padding each of these nodes separately, a
  // better experience is produced.
  // Even though this does DOM manipulation, it's written safely in case
  // the nodes don't exist. It also shouldn't needlessly reapply the effect.
  // This component and effect were written to be easily removable.
  useEffect(() => {
    const shouldAccommodateSidebar =
      isSidebarRendered && !isSidebarControlledByTopbar;
    [
      document.querySelector('#static-content'),
      document.querySelector('#react-root-app'),
      document.querySelector('#footer'),
    ]
      .filter(Boolean)
      .forEach(node => {
        node.classList.toggle(
          `server-node--accommodate-universal-sidebar`,
          shouldAccommodateSidebar
        );
        node.style.paddingLeft = `${sidebarWidth}px`;
      });
  }, [isSidebarRendered, isSidebarControlledByTopbar, sidebarWidth]);
  return (
    <Fragment>
      <Global styles={Styles} />
      {props.children}
    </Fragment>
  );
};

export default WebUniversalNavPageLayout;
